"use client"

import CaptionElement from "@/app/(site)/_components/GCMolecules/CaptionElement"
import VideoControls from "@/app/(site)/_components/SanityElements/VideoControls"
import { getVideoPosterUrl } from "@/src/utils/video/get-video-poster-url"
import { type ImageType } from "@/types"
import { useEffect, useRef, useState } from "react"

interface VimeoVideoElementProps {
    vimeoId: string
    caption?: string
    autoplay: boolean
    mute: boolean
    hasSound: boolean
    hideControls?: boolean
    loop?: boolean
    poster?: ImageType | null
}

export default function VimeoVideoElement({
    caption,
    vimeoId,
    autoplay = true,
    mute = true,
    hasSound = false,
    hideControls = false,
    loop = false,
    poster
}: VimeoVideoElementProps) {
    const [isPlaying, setIsPlaying] = useState(autoplay && mute)
    const [isMuted, setIsMuted] = useState(mute)
    const [hasLoaded, setHasLoaded] = useState(false)
    const videoRef = useRef<HTMLVideoElement>(null)
    const videoContainerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const container = videoContainerRef.current
        if (!container) return

        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    const isVisible = entry.isIntersecting

                    if (isVisible && !hasLoaded) {
                        setHasLoaded(true)
                    }

                    if (autoplay && mute && videoRef.current && hasLoaded) {
                        if (isVisible) {
                            videoRef.current.play().catch(() => {
                                console.error("Video playback failed")
                            })
                        } else {
                            videoRef.current.pause()
                        }
                    }
                })
            },
            {
                rootMargin: "100px",
                threshold: 0.1
            }
        )

        observer.observe(container)

        return () => {
            observer.disconnect()
        }
    }, [autoplay, mute, hasLoaded])

    const toggleVideoPlayPause = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault()
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause()
                setIsPlaying(false)
            } else {
                videoRef.current.play()
                setIsPlaying(true)
            }
        }
    }

    const toggleVideoMute = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault()
        if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted
            setIsMuted(videoRef.current.muted)
        }
    }

    const handleVideoPlay = () => setIsPlaying(true)
    const handleVideoPause = () => setIsPlaying(false)

    const posterUrl = getVideoPosterUrl(poster)

    return (
        <>
            <div className="relative flex h-full w-full flex-col gap-y-8">
                <div
                    ref={videoContainerRef}
                    className="relative h-full w-full overflow-hidden"
                >
                    <video
                        ref={videoRef}
                        autoPlay={false}
                        muted={isMuted}
                        playsInline
                        loop={loop}
                        poster={posterUrl}
                        preload="none"
                        className="absolute inset-0 h-full w-full object-cover"
                        style={{
                            objectPosition: "50% 50%"
                        }}
                        onPlay={handleVideoPlay}
                        onPause={handleVideoPause}
                    >
                        {hasLoaded && <source src={vimeoId} type="video/mp4" />}
                        Your browser does not support the video tag.
                    </video>
                    <VideoControls
                        isPlaying={isPlaying}
                        isMuted={isMuted}
                        hasSound={hasSound}
                        autoplay={autoplay}
                        hideControls={hideControls}
                        onTogglePlayPause={toggleVideoPlayPause}
                        onToggleMute={toggleVideoMute}
                    />
                </div>
            </div>
            {caption && <CaptionElement caption={caption} />}
        </>
    )
}
